/* =====================================================================
 * WorkspaceAdaptersProvider — the sole DI entry point for
 * `@auw/workspace-app`.
 *
 * Host apps (`askyourai`, `apps/enterprise`) wrap the workspace tree
 * with this provider and pass in their concrete store hooks, API
 * clients, and route entries. Every component inside this package
 * reads dependencies through `useWorkspaceAdapters()` instead of
 * importing from app-local paths, keeping the package host-agnostic.
 *
 * Design: single Context (not one per concern) so depth of providers
 * stays bounded. See `types.ts` for the shape of `WorkspaceAdapters`.
 * ===================================================================== */

import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';

import type { WorkspaceAdapters } from './types';

const WorkspaceAdaptersContext = createContext<WorkspaceAdapters | null>(null);

export interface WorkspaceAdaptersProviderProps {
  readonly adapters: WorkspaceAdapters;
  readonly children: ReactNode;
}

export function WorkspaceAdaptersProvider({
  adapters,
  children,
}: WorkspaceAdaptersProviderProps): JSX.Element {
  /* `useMemo` avoids re-creating the context value on every render of
   * the host app when `adapters` is reference-stable — cheap, but
   * meaningful once real adapters grow to O(dozens) of members. */
  const value = useMemo(() => adapters, [adapters]);
  return (
    <WorkspaceAdaptersContext.Provider value={value}>
      {children}
    </WorkspaceAdaptersContext.Provider>
  );
}

/**
 * Read the host-app-supplied adapters. Throws if called outside a
 * `<WorkspaceAdaptersProvider>` so missing wiring fails loud in dev
 * rather than surfacing as a mysterious `undefined.useFoo()`.
 */
export function useWorkspaceAdapters(): WorkspaceAdapters {
  const ctx = useContext(WorkspaceAdaptersContext);
  if (!ctx) {
    throw new Error(
      '[@auw/workspace-app] useWorkspaceAdapters() must be called inside <WorkspaceAdaptersProvider>.',
    );
  }
  return ctx;
}
